import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PossibleVariants } from "@/components/app/PossibleVariants";
import { Fingerprint, Tag, Camera, Info } from "lucide-react";

const Section = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="border border-shop-strong bg-elevated">
    <div className="flex items-center gap-2 px-4 py-2 border-b border-shop-strong bg-shop">
      <Icon size={14} className="text-amber-brass" />
      <span className="font-display uppercase tracking-widest text-sm text-cream">{title}</span>
    </div>
    <div className="p-4 space-y-2">{children}</div>
  </div>
);

const KeyValueList = ({ pairs }) => (
  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
    {pairs.map(([k, v]) => (
      <div key={k} className="text-xs">
        <span className="font-mono text-amber-brass uppercase tracking-widest mr-2">{k}:</span>
        <span className="text-cream break-words">{v || "—"}</span>
      </div>
    ))}
  </div>
);

const VinDetail = ({ vin_decoded }) => {
  if (!vin_decoded) return null;
  const era = vin_decoded.era;
  const classic = vin_decoded.classic_decode?.decoded;
  const nhtsa = vin_decoded.nhtsa || {};
  const summary = vin_decoded.ai_summary;
  const positions = vin_decoded.classic_decode?.position_breakdown || classic?.position_breakdown || [];

  const pairs = [];
  if (era === "modern") {
    ["ModelYear", "Make", "Model", "Trim", "BodyClass", "EngineModel", "PlantCity"].forEach((k) => {
      if (nhtsa[k]) pairs.push([k, nhtsa[k]]);
    });
  } else if (classic) {
    if (classic.model_year) pairs.push(["Model year", classic.model_year]);
    if (classic.make) pairs.push(["Make", classic.make]);
    if (classic.model) pairs.push(["Model", classic.model]);
    if (classic.body_style) pairs.push(["Body style", classic.body_style]);
    if (classic.engine) pairs.push(["Engine", classic.engine]);
    if (classic.assembly_plant) pairs.push(["Assembly plant", classic.assembly_plant]);
    if (classic.serial_number) pairs.push(["Serial #", classic.serial_number]);
    if (classic.special_variant) pairs.push(["Variant", classic.special_variant]);
    if (classic.confidence) pairs.push(["Confidence", classic.confidence]);
  }

  return (
    <Section icon={Fingerprint} title="VIN Decoded" testid="detail-vin">
      {vin_decoded.vin && (
        <div className="font-mono text-[10px] text-secondary break-all mb-2">{vin_decoded.vin}</div>
      )}
      {pairs.length > 0 && <KeyValueList pairs={pairs} />}
      {positions?.length > 0 && (
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-brass mb-1">
            Position breakdown
          </div>
          <div className="border border-shop-strong">
            {positions.map((p, i) => (
              <div key={`${p.position || i}-${p.value || ""}`} className="grid grid-cols-[auto,80px,1fr] gap-2 border-b border-shop-strong last:border-b-0 px-2 py-1 text-xs">
                <span className="font-mono text-secondary">#{p.position}</span>
                <span className="font-mono text-amber-brass">{p.value}</span>
                <span className="text-cream">{p.meaning}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {classic?.possible_variants?.length > 0 && (
        <PossibleVariants variants={classic.possible_variants} title="" subtitle="Options that were available for this model+year." />
      )}
      {summary && (
        <div className="mt-3 border-l-2 border-[var(--accent-amber)] pl-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-brass mb-1">AI Summary</div>
          <p className="text-xs text-cream whitespace-pre-line">{summary}</p>
        </div>
      )}
    </Section>
  );
};

const TagDetail = ({ tag_decoded }) => {
  if (!tag_decoded) return null;
  const d = tag_decoded.decoded || tag_decoded;
  return (
    <Section icon={Tag} title="Body Tag Decoded" testid="detail-tag">
      {d.tag_type && (
        <div className="text-xs">
          <span className="font-mono text-amber-brass uppercase tracking-widest mr-2">Tag type:</span>
          <span className="text-cream">{d.tag_type}</span>
        </div>
      )}
      {d.summary && <p className="text-xs text-cream whitespace-pre-line">{d.summary}</p>}
      {d.fields?.length > 0 && (
        <div className="border border-shop-strong mt-2">
          {d.fields.map((f, i) => (
            <div key={`${f.code || i}-${f.label || ""}`} className="grid grid-cols-[110px,1fr] gap-2 border-b border-shop-strong last:border-b-0 px-2 py-1 text-xs">
              <span className="font-mono text-amber-brass">{f.code || f.label}</span>
              <span className="text-cream">
                {f.label && f.code ? <span className="text-secondary">{f.label}: </span> : null}
                {f.value || f.meaning || "—"}
              </span>
            </div>
          ))}
        </div>
      )}
      {d.special_variant && (
        <div className="mt-2 inline-block border-l-2 border-[var(--accent-red)] bg-[rgba(217,32,39,0.1)] px-2 py-1">
          <span className="font-display uppercase text-sm text-cream">{d.special_variant}</span>
        </div>
      )}
    </Section>
  );
};

const PhotoDetail = ({ photo_identified }) => {
  if (!photo_identified) return null;
  const p = photo_identified;
  const pairs = [];
  ["year", "make", "model", "trim", "body_style", "engine", "special_variant", "confidence"].forEach((k) => {
    if (p[k]) pairs.push([k.replace("_", " "), p[k]]);
  });
  return (
    <Section icon={Camera} title="Photo ID" testid="detail-photo">
      {pairs.length > 0 && <KeyValueList pairs={pairs} />}
      {p.notes && <p className="text-xs text-cream whitespace-pre-line mt-2">{p.notes}</p>}
    </Section>
  );
};

export const GarageDetailDialog = ({ vehicle, onClose }) => {
  if (!vehicle) return null;
  return (
    <Dialog open={Boolean(vehicle)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="garage-detail-dialog"
        className="max-w-3xl max-h-[85vh] overflow-y-auto bg-shop border-shop-strong"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-2xl text-cream">
            {vehicle.year || "—"} {vehicle.make} {vehicle.model}
            {vehicle.trim && <span className="text-amber-brass"> — {vehicle.trim}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {vehicle.has_photo && vehicle.photo_base64 && (
            <img
              src={`data:${vehicle.photo_mime || "image/jpeg"};base64,${vehicle.photo_base64}`}
              alt=""
              className="w-full max-h-72 object-contain border border-shop-strong"
            />
          )}

          <VinDetail vin_decoded={vehicle.vin_decoded} />
          <TagDetail tag_decoded={vehicle.tag_decoded} />
          <PhotoDetail photo_identified={vehicle.photo_identified} />

          {vehicle.confirmed_options?.length > 0 && (
            <Section icon={Info} title="Confirmed on this car" testid="detail-options">
              <div className="flex flex-wrap gap-1.5">
                {vehicle.confirmed_options.map((opt) => (
                  <span
                    key={opt}
                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[var(--accent-amber)] text-amber-brass bg-elevated"
                  >
                    {opt}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {vehicle.notes && (
            <Section icon={Info} title="Notes" testid="detail-notes">
              <p className="text-xs text-cream whitespace-pre-line">{vehicle.notes}</p>
            </Section>
          )}

          {!vehicle.vin_decoded && !vehicle.tag_decoded && !vehicle.photo_identified && (
            <div className="text-xs text-secondary italic text-center py-4">
              This entry was saved with only basic info (VIN string, year/make/model).
              Decode it again from the VIN, Tag, or Photo tabs to attach full details.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GarageDetailDialog;
